<template>
  <Form>
    <template #header>
      {{ `${mode} ${$t('projects.form.secondStep.labelLastPart')}` }}
    </template>

    <div class="d-flex is-indent select-date">
      <div class="d-flex flex-column is-line">
        <AuInput
          :label="$t('projects.form.secondStep.projectDateStartLabel')"
          :model-value="dateStart"
          :min-date="minDate"
          :format="formatView"
          :enable-minutes="false"
          type="Date"
          enable-time-picker
          required
          clearable
          :placeholder="$t('projects.form.secondStep.projectDateStartPlaceholder')"
          @update:modelValue="setDateStart"
        />
      </div>
      <div class="d-flex flex-column is-line">
        <AuInput
          :label="$t('projects.form.secondStep.projectDateEndLabel')"
          :model-value="dateEnd"
          :min-date="minDateAfterMinDate"
          :format="formatView"
          :enable-minutes="false"
          type="Date"
          enable-time-picker
          :required="!isFoundation"
          :readonly="isFoundation"
          clearable
          :placeholder="$t('projects.form.secondStep.projectDateEndPlaceholder')"
          @update:modelValue="setDateEnd"
        />
      </div>
    </div>
    <div class="d-flex is-indent select-date">
      <div class="d-flex flex-column is-line">
        <AuInput
          :label="$t('First payout')"
          :model-value="firstPayout"
          :min-date="minDate"
          :format="formatView"
          :enable-minutes="false"
          type="Date"
          enable-time-picker
          required
          clearable
          :placeholder="$t('Enter first payout')"
          @update:modelValue="setFirstPayout"
        />
      </div>
    </div>

    <div
      v-if="!showPriceRedeem"
      class="is-label"
    >
      <div
        v-if="firstPartOfProjectInfo?.applicationType == 'stakingPool'"
        class="label-name"
      >
        {{ $t('projects.form.secondStep.projectAPYLabel') }}
      </div>
      <div
        v-else
        class="label-name"
      >
        {{ $t('projects.form.secondStep.projectExpectedProfitLabel') }}<AuTooltip :description="' Expected value in percents which could be achieved in the year range.'" />
      </div>
    </div>
    <div
      v-if="!showPriceRedeem"
      class="is-indent"
    >
      <AuInput
        v-model="expectedProfit"
        type="Number"
        required
        clearable
        :min="0"
        :max="100"
        :placeholder="firstPartOfProjectInfo?.applicationType == 'stakingPool' ? $t('projects.form.secondStep.projectAPYPlaceholder') : $t('projects.form.secondStep.projectExpectedProfitPlaceholder')"
      />
    </div>
    <div class="is-types">
      <div class="is-label label-section">
        {{ $t('projects.form.secondStep.projectSectionLabel') }}
      </div>
      <div class="is-indent input-section">
        <AuSelect
          :value="section"
          :options="capitalsSectionsOptions"
          required
          clearable
          :placeholder="$t('projects.form.secondStep.projectSectionPlaceholder')"
          @input="value => section = value"
        />
      </div>
      <div class="is-label label-asset-types">
        {{ $t('projects.form.secondStep.projectAssetTypeLabel') }}
      </div>
      <div class="is-indent input-asset-types is-select">
        <AuInput
          v-model="assetType"
          required
          clearable
          :placeholder="$t('projects.form.secondStep.projectSectionPlaceholder')"
        />
      </div>
    </div>
    <div
      v-if="showPriceRedeem"
      class="is-indent"
    >
      <AuInput
        v-model="priceRedeem"
        :label="$t('projects.form.secondStep.projectPriceRedeemLabel')"
        required
        :placeholder="$t('projects.form.secondStep.projectPriceRedeemPlaceholder')"
        type="Mask"
        :mask="Number"
      />
    </div>

    <div
      v-if="showPriceRedeem"
      class="is-indent"
    >
      <AuInput
        :label="$t('projects.form.secondStep.projectDateCloseLabel')"
        :model-value="dateClose"
        :min-date="minDateAfterMinDate"
        :format="formatView"
        :enable-minutes="false"
        enable-time-picker
        type="Date"
        required
        clearable
        :placeholder="$t('projects.form.secondStep.projectDateClosePlaceholder')"
        @update:modelValue="setDateClose"
      />
    </div>

    <div class="is-indent">
      <AuInput
        v-model="projectTerms"
        :label="$t('projects.form.secondStep.projectTermsLabel')"
        type="TextArea"
        text-area-heigth="75"
        clearable
        :placeholder="$t('projects.form.secondStep.projectTermsPlaceholder')"
      />
    </div>
    <div v-if="firstPartOfProjectInfo?.applicationType == 'stakingPool'">
      <div class="is-indent">
        <AuInput
          v-model="lockUpTerm"
          :label="$t('projects.form.secondStep.lockUpTerm')"
          :placeholder="$t('projects.form.secondStep.lockUpTermPlaceholder')"
          type="Number"
          :decimal="0"
        />
      </div>

      <div class="is-indent">
        <AuInput
          v-model="withdrawalDelayTime"
          :label="$t('projects.form.secondStep.withdrawalDelayTime')"
          :placeholder="$t('projects.form.secondStep.withdrawalDelayTimePlaceholder')"
          type="Number"
        />
      </div>

      <div class="is-indent">
        <AuInput
          v-model="bonusMultiplier"
          :label="$t('projects.form.secondStep.bonusMultiplier')"
          :placeholder="$t('projects.form.secondStep.bonusMultiplierPlaceholder')"
          type="Number"
          :decimal="2"
          :max="9999"
        />
      </div>
    </div>
    <div
      class="is-indent is-advanced"
      @click="showAdvanced=!showAdvanced"
    >
      <span>
        {{ $t("projects.form.secondStep.projectAdvanced") }}
      </span>
      <AuIcon
        :icon="showAdvanced ? 'chevron_up' : 'chevron_down'"
        clickable
        @click="showAdvanced=!showAdvanced"
      />
    </div>
    <div
      v-show="showAdvanced"
      class="is-indent"
    >
      <div class="is-small-indent">
        <AuInput
          v-model="priceBase"
          :label="$t('projects.form.secondStep.projectPriceBaseLabel') + priceInMnemonic "
          required
          clearable
          :placeholder="$t('projects.form.secondStep.projectPriceBasePlaceholder')"
          type="Number"
          :decimal="currentTokenDecimals"
        />
      </div>

      <div class="is-indent">
        <AuInput
          v-model="minCol"
          :label="$t('projects.form.secondStep.projectMinNumLabel')"
          :has-error="minColHasError"
          :placeholder="$t('projects.form.secondStep.projectMinNumPlaceholder')"
          type="Mask"
          :mask="Number"
          :readonly="isFoundation"
          :max="tokensCount"
        />
      </div>
      <div
        v-if="firstPartOfProjectInfo?.applicationType == 'stakingPool'"
        class="d-flex is-indent select-cap"
      >
        <div class="is-line is-minmax">
          <span class="is-text-inside">{{ $t('projects.form.secondStep.projectMinPriceLabel') }}</span>
          <AuDecimalStatic
            :value="minCap"
            :addition="priceInMnemonic"
            :show-addition="!!priceInMnemonic"
            :decimal="currentTokenDecimals"
          />
        </div>
        <div class="is-line is-minmax">
          <span class="is-text-inside">{{ $t('projects.form.secondStep.projectMaxPriceLabel') }}</span>
          <AuDecimalStatic
            :value="maxCap"
            :addition="priceInMnemonic"
            :show-addition="!!priceInMnemonic"
            :decimal="currentTokenDecimals"
          />
        </div>
      </div>
      <div
        v-else
        class="d-flex is-indent select-cap"
      >
        <div class="is-line-100 is-minmax">
          <span class="is-text-inside">{{ $t('projects.form.secondStep.projectMaxPriceLabel') }}</span>
          <AuDecimalStatic
            :value="maxCap"
            :addition="priceInMnemonic"
            :show-addition="!!priceInMnemonic"
            :decimal="currentTokenDecimals"
          />
        </div>
      </div>

      <div class="is-indent">
        <AuInput
          v-model="minCount"
          :label="$t('projects.form.secondStep.projectLimitMinCount')"
          :has-error="minCountHasError"
          clearable
          :placeholder="$t('projects.form.secondStep.projectLimitMinCountPlaceholder')"
          @input="checkMinCount"
        />
      </div>

      <div class="is-indent">
        <AuInput
          v-model="quote"
          :label="$t('projects.form.secondStep.projectLimitOneHandLabel')"
          :has-error="quoteHasError"
          clearable
          type="NullableNumber"
          :placeholder="$t('projects.form.secondStep.projectLimitPlaceholder')"
          :max="maxCountDecimal"
          @input="checkQuote"
        />
      </div>
    </div>
    <div
      v-if="isTestRole"
      class="is-indent is-advanced"
      @click="showTest=!showTest"
    >
      <span>
        {{ $t("projects.form.secondStep.projectTest") }}
      </span>
      <AuIcon
        :icon="showTest ? 'chevron_up' : 'chevron_down'"
        clickable
        @click="showTest=!showTest"
      />
    </div>
    <div v-if="showTest">
      <div class="is-label">
        {{ $t('projects.form.secondStep.projectTokenQuoteLabel') }}<AuTooltip :description="'<div>Project token determined by project type:</div><li>USD — for investment project</li><li>AUT — for voting project</li>'" />
      </div>
      <div class="is-indent">
        <AuSelect
          :value="tokenQuote"
          :options="tokenQuotesOptions"
          required
          :placeholder="$t('projects.form.secondStep.projectTokenQuotePlaceholder')"
          @input="value => tokenQuote = value"
        />
      </div>

      <div class="is-small-indent">
        <AuInput
          v-model="systemСomission"
          :label="$t('projects.form.secondStep.projectSystemСomission') + '%'"
          clearable
          :placeholder="$t('projects.form.secondStep.projectSystemСomissionPlaceholder')"
          @input="checkSystemСomission"
        />
      </div>
    </div>

    <div
      v-if="showError"
      class="error"
    >
      {{ errorMsg }}
    </div>
    <template #footer>
      <AuButton
        bordered
        center
        @click="prevStep"
      >
        {{ $t("projects.form.backButton") }}
      </AuButton>

      <AuButton
        type="primary"
        center
        @click="nextStep"
      >
        {{ $t("projects.form.nextButton") }}
      </AuButton>
    </template>
  </Form>
</template>

<script>
import _ from "lodash";
import BigNumber from "bignumber.js";
import moment from "moment";
import { mapActions, mapGetters } from "vuex";
import "@vuepic/vue-datepicker/dist/main.css";

export default {
  props: {
    id: {
      type: String,
      default: "new"
    },
    tokensCount: {
      type: String,
      default: null
    },
    showPriceRedeem: {
      type: Boolean,
      default: false
    },
    firstPartOfProjectInfo: {
      type: Object,
      default: () => {}
    },
    secondPartOfProjectInfo: {
      type: Object,
      default: () => {}
    }
  },

  data() {
    return {
      timeout: null,
      minCount: null,
      minCountHasError: false,
      minCol: 0,
      minColHasError: false,
      dateStart: null,
      dateEnd: null,
      firstPayout: null,
      section: null,
      assetType: null,
      priceBase: 0,
      systemСomission: "0",
      tokenMnemonic: "",
      priceRedeem: null,
      quote: null,
      quoteHasError: false,
      expectedProfit: null,
      dateClose: null,
      tokenQuote: null,
      showAdvanced: false,
      showTest: false,
      showError: false,
      errorMsg: null,
      lockUpTerm: null,
      withdrawalDelayTime: null,
      bonusMultiplier: null,
      isFoundation: false,
      projectTerms: null
    };
  },

  computed: {
    ...mapGetters({
      tokensFiat: "tokensFiat",
      tokensGold: "tokensGold",
      tokensUtility: "tokensUtility",
      sectionCapital: "sectionCapital",
      defaultAutMnemonic: "defaultToken/defaultAutMnemonic",
      defaultTokenEnv: "defaultToken/defaultTokenEnv",
      isTestRole: "profile/isTestRole",
      profile: "profile/profile"
    }),

    minDate() {
      return new Date(new Date().getTime()
      - new Date().getSeconds() * 1000
      - new Date().getMinutes() * 60000
      - new Date().getHours() * 3600000);
    },

    minDateAfterMinDate() {
      if (this.dateStart) {
        this.dateStart.setMinutes(0, 0, 0);
      }
      return this.dateStart
        ? new Date(this.dateStart.getTime() + (24 * 60 * 60 * 1000) * 2)
        : new Date(this.minDate.getTime() + (24 * 60 * 60 * 1000) * 2);
    },

    tokenQuotesOptions() {
      const buf = new Set();
      _.forEach([...this.tokensFiat, ...this.tokensUtility], token => buf.add(token));
      return _.map([...buf.values()], token => ({ display: token.nameEng, value: `/api/au_tokens/${token.id}`, decimals: token.decimals }));
    },

    priceInMnemonic() {
      const { projectType, applicationType } = this.firstPartOfProjectInfo || {};
      if (projectType === "voting" || applicationType === "stakingPool") {
        return this.defaultAutMnemonic;
      }
      return this.defaultTokenEnv;
    },

    currentTokenDecimals() {
      const token = _.find(this.tokenQuotesOptions, { value: this.tokenQuote });
      if (token) {
        return token.decimals;
      }
      return 0;
    },

    capitalsSectionsOptions() {
      return _.map(this.sectionCapital, section => ({ display: section.name, value: `/api/section_capitals/${section.id}` }));
    },

    isLightTheme() {
      return this.$store.getters.isLightTheme;
    },

    minCap() {
      const res = this.tokenQuote ? BigNumber(this.minCol).multipliedBy(this.priceBase).toString(10) : null;
      return !isNaN(res) ? res : 0;
    },

    maxCap() {
      const res = this.tokenQuote ? BigNumber(this.tokensCount).multipliedBy(this.priceBase).toString(10) : 0;
      return !isNaN(res) && res != 0 ? res : null;
    },

    mode() {
      return this.id === "new" ?
        this.$t("projects.form.secondStep.labelNew") :
        this.$t("projects.form.secondStep.labelEdit");
    },

    profit() {
      if (!this.showPriceRedeem && this.expectedProfit) {
        return this.expectedProfit.replace(",", ".");
      }
      const pricePercent = ((this.priceRedeem - this.priceBase) * 100) / this.priceBase;
      const yearCoefficient = Math.floor(365 / moment(this.dateClose).diff(this.dateEnd, "days"));
      return pricePercent * yearCoefficient;
    },

    requiredFields() {
      let fields = [
        { label: this.$t("projects.form.secondStep.projectDateStartLabel"), value: this.dateStart },
        { label: this.$t("projects.form.secondStep.projectExpectedProfitLabel"), value: this.expectedProfit },
        { label: this.$t("projects.form.secondStep.projectSectionLabel"), value: this.section },
        { label: this.$t("projects.form.secondStep.projectPriceBaseLabel"), value: this.priceBase },
        { label: this.$t("projects.form.secondStep.projectAssetTypeLabel"), value: this.assetType }
      ];
      if (!this.isFoundation) {
        fields.splice(1, 0, { label: this.$t("projects.form.secondStep.projectDateEndLabel"), value: this.dateEnd });
      }
      return fields;
    },

    assetTypesOptions() {
      return process.env.VUE_APP_CAPITAL_ASSET_TYPES ?? "none";
    },

    maxCountDecimal() {
      return this.maxCap ? Math.floor(Number(this.maxCap) / Math.pow(10, this.currentTokenDecimals)) : 0;
    }
  },

  async mounted() {
    const envToken = this.firstPartOfProjectInfo?.projectType == "voting" || this.firstPartOfProjectInfo?.applicationType == "stakingPool" ? this.defaultAutMnemonic : this.defaultTokenEnv;
    this.tokenQuote = `/api/au_tokens/${_.find(this.tokensUtility, item => item.mnemonicEng === envToken)?.id}`;
    this.assetType = this.assetTypesOptions;
    this.isFoundation = this.firstPartOfProjectInfo?.applicationType == "foundation" ? true : false;

    if (this.secondPartOfProjectInfo) {
      if (this?.secondPartOfProjectInfo.tokenDecimals !== this.currentTokenDecimals) {
        const priceCorrect = BigNumber(this.secondPartOfProjectInfo.priceBase).dividedBy(BigNumber(10).pow(this?.secondPartOfProjectInfo.tokenDecimals));
        this.secondPartOfProjectInfo.priceBase = BigNumber(priceCorrect).multipliedBy(BigNumber(10).pow(this.currentTokenDecimals)).toString(10);
      }
      else {
        this.tokenQuote = this.secondPartOfProjectInfo.tokenQuote;
      }
      this.projectTerms = this.secondPartOfProjectInfo.projectTerms;
      this.minCount = this.secondPartOfProjectInfo?.minCount;
      this.minCol = this.secondPartOfProjectInfo.minCol;
      this.dateStart = this.secondPartOfProjectInfo.dateStart;
      this.dateEnd = this.secondPartOfProjectInfo.dateEnd;
      this.firstPayout = this.secondPartOfProjectInfo?.firstPayout;
      this.section = this.secondPartOfProjectInfo.section;
      this.assetType = this.secondPartOfProjectInfo.assetType;
      this.priceBase = this.secondPartOfProjectInfo.priceBase;
      this.quote = this.secondPartOfProjectInfo.quote;
      this.systemСomission = this.secondPartOfProjectInfo.feePercent;
      this.lockUpTerm = this.secondPartOfProjectInfo.lockUpTerm;
      this.withdrawalDelayTime = this.secondPartOfProjectInfo.withdrawalDelayTime;
      this.bonusMultiplier = this.secondPartOfProjectInfo.bonusMultiplier;
      this.tokenMnemonic = this.secondPartOfProjectInfo.tokenMnemonic;
      this.expectedProfit = this.secondPartOfProjectInfo.expectedProfit ? (this.secondPartOfProjectInfo.expectedProfit).toString(10).replace(".", ",") : "";
      if (this.showPriceRedeem) {
        this.priceRedeem = this.secondPartOfProjectInfo.priceRedeem;
        this.dateClose = this.secondPartOfProjectInfo.dateClose;
      }
    }

    if (_.isEmpty(this.sectionCapital)) {
      await this.getSectionCapital();
    }
    if (this.id !== "new" && !this.secondPartOfProjectInfo) {
      const requestToIssue = await this.getRequestToIssue(this.id);
      this.setFields(requestToIssue);
    }
    if (!this.dateStart) {
      this.dateStart = new Date();
    }
    if (this.priceBase == 0 || this.priceBase == "NaN") {
      this.priceBase = BigNumber(1).multipliedBy(BigNumber(10).pow(this.currentTokenDecimals)).toString(10);
    }
    this.checkEnvToken();
  },

  methods: {
    ...mapActions({
      getRequestToIssue: "getRequestToIssue",
      getSectionCapital: "getSectionCapital"
    }),

    checkSystemСomission(event) {
      let value = event.target.value;
      value = value.replace(/[^0-9.]/g, "");
      const parts = value.split(".");
      if (parts.length > 2) {
        value = parts[0] + "." + parts.slice(1).join("");
      }
      const numericValue = parseFloat(value);
      if (numericValue < 0) {
        value = "0";
      }
      else if (numericValue > 33) {
        value = "33";
      }
      this.systemСomission = value;
    },

    checkEnvToken() {
      const { projectType, applicationType } = this.firstPartOfProjectInfo || {};
      if (projectType === "invest") {
        this.setTokenQuote(this.defaultTokenEnv);
      }
      else if (projectType === "voting" || applicationType === "stakingPool") {
        this.setTokenQuote(this.defaultAutMnemonic);
      }
    },

    checkRequiredFields() {
      const res = _.find(this.requiredFields, field => !field.value || field.value === "");
      this.errorMsg = res ? this.$t("projects.form.error") + ` ${res.label}` : null;
      return res === undefined;
    },

    setDateStart(value) {
      this.dateStart = this.getDate(value);

      if (this.dateEnd && this.dateEnd.getTime() < this.minDateAfterMinDate.getTime()) {
        this.dateEnd = null;
      }

      if (this.dateClose && this.dateClose.getTime() < this.minDateAfterMinDate.getTime()) {
        this.dateClose = null;
      }
    },

    setDateEnd(value) {
      this.dateEnd = this.getDate(value);
    },

    setFirstPayout(value) {
      this.firstPayout = this.getDate(value);
    },

    setDateClose(value) {
      this.dateClose = this.getDate(value);
    },

    getDate(value) {
      if (!value) {
        return value;
      }

      value = new Date(value.getTime() - 20 * 60000);

      if (value.getMinutes() < 30) {
        return new Date(value.getTime()
        - value.getSeconds() * 1000
        - value.getMinutes() * 60000);
      }
      else {
        return new Date(value.getTime()
        + (60 - value.getSeconds()) * 1000
        + (59 - value.getMinutes()) * 60000);
      }
    },

    checkInput(value) {
      const buf = value.match(/^[0-9]{1,}[.,]?[0-9]{0,2}/g);
      return buf ? buf[0].replace(",", ".") : null;
    },

    checkQuote() {
      if (this.timeout) {
        clearTimeout(this.timeout);
      }

      this.timeout = setTimeout(() => {
        this.quote = this.checkInput(this.quote);

        if (this.quote && BigNumber(this.quote).isGreaterThan(this.tokensCount)) {
          this.quote = this.tokensCount.toString();
        }

        if (this.quote && BigNumber(this.quote).isLessThan(this.minCount)) {
          this.quote = this.minCount.toString();
        }
      }, 500);
    },

    checkMinCount() {
      this.minCount = this.checkInput(this.minCount);
      if (this.minCount && BigNumber(this.minCount).isGreaterThan(this.tokensCount)) {
        this.minCount = this.minCount.slice(0, -1);
      }
    },

    checkTokensIsInteger() {
      if (this.minCol) {
        this.minColHasError = !BigNumber(this.minCol).isInteger();
      }
      if (this.minCount) {
        this.minCountHasError = !BigNumber(this.minCount).isInteger();
      }
      if (this.quote) {
        this.quoteHasError = !BigNumber(this.quote).isInteger();
      }
      return this.minColHasError || this.minCountHasError || this.quoteHasError;
    },


    prevStep() {
      let secondPartOfProjectInfo = {
        minCount: this.minCount,
        minCol: this.minCol,
        dateStart: this.dateStart,
        dateEnd: this.dateEnd,
        firstPayout: this.firstPayout,
        section: this.section,
        assetType: this.assetType,
        priceBase: this.priceBase,
        feePercent: this.systemСomission,
        lockUpTerm: this.lockUpTerm,
        withdrawalDelayTime: this.withdrawalDelayTime,
        bonusMultiplier: this.bonusMultiplier,
        tokenQuote: this.tokenQuote,
        quote: this.quote,
        expectedProfit: this.profit.toString() == 0 ? "" : this.profit.toString(),
        tokenMnemonic: this.tokenMnemonic,
        tokenDecimals: this.currentTokenDecimals
      };
      if (this.showPriceRedeem) {
        secondPartOfProjectInfo.priceRedeem = this.priceRedeem;
        secondPartOfProjectInfo.dateClose = this.dateClose;
      }

      this.$emit("secondPartOfProjectInfo", secondPartOfProjectInfo);
      this.$emit("prevStep");
    },

    formatView(date) {
      return moment(date).locale("ru").format("L HH:mm");
    },

    setTokenQuote(value) {
      this.tokenMnemonic = value;
    },

    nextStep() {
      let secondPartOfProjectInfo = {
        dateStart: this.dateStart,
        dateEnd: this.dateEnd,
        firstPayout: this.firstPayout,
        section: this.section,
        assetType: this.assetType,
        terms: this.projectTerms,
        priceBase: this.priceBase,
        tokenQuote: this.tokenQuote,
        tokenMnemonic: this.tokenMnemonic,
        maxCap: this.maxCap,
        expectedProfit: this.profit.toString(),
        quote: this.quote,
        minCount: this.minCount,
        feePercent: this.systemСomission,
        lockUpTerm: this.lockUpTerm,
        withdrawalDelayTime: this.withdrawalDelayTime,
        bonusMultiplier: this.bonusMultiplier,
        tokenDecimals: this.currentTokenDecimals
      };
      if (this.minCol) {
        secondPartOfProjectInfo.minCol = this.minCol;
      }
      if (this.minCap && Number(this.minCap) > 0 && !this.isFoundation) {
        secondPartOfProjectInfo.minCap = this.minCap;
      }
      if (this.showPriceRedeem) {
        secondPartOfProjectInfo.priceRedeem = this.priceRedeem;
        secondPartOfProjectInfo.dateClose = this.dateClose;
      }
      if (this.checkRequiredFields()) {
        if (this.checkTokensIsInteger()) {
          this.showError = true;
          this.errorMsg = this.$t("projects.form.secondStep.tokenIntegerError");
          return;
        }
        this.$emit("secondPartOfProjectInfo", secondPartOfProjectInfo);
        this.$emit("nextStep");
      }
      else {
        this.showError = true;
      }
    },

    goBack() {
      this.$router.push({ name: "my projects" });
    },

    setFields(data) {
      this.minCount = data?.minCount;
      this.minCol = isNaN(BigNumber(data?.minCap).dividedBy(data.priceBase).toString(10)) ? "" : BigNumber(data?.minCap).dividedBy(data.priceBase).toString(10);
      this.dateStart = new Date(Date.parse(data.dateStart));
      this.dateEnd = new Date(Date.parse(data.dateEnd));
      this.firstPayout = new Date(Date.parse(data?.firstPayout));
      this.section = data.section;
      this.projectTerms = data.terms;
      this.assetType = data.assetType;
      this.priceBase = data.priceBase;
      this.tokenQuote = data.tokenQuote;
      this.quote = data.quote;
      this.expectedProfit = data.expectedProfit.toString(10).replace(".", ",");
      this.setTokenQuote(data.tokenQuote);
      if (this.showPriceRedeem) {
        this.priceRedeem = data.priceRedeem;
        this.dateClose = new Date(Date.parse(data.dateClose));
      }
    }
  }
};
</script>

<style lang="scss">
.is2{
  font-weight: 400;
  font-size: 14px;
  padding: 20px;
}
.is-label{
  margin-bottom: 8px;
  font-size: 14px;
  color: var(--color-50);
}
.is-line{
  width: 50%;
  height: 44px;
  @media screen and (max-width: 1000px) {
    width: 100%;
  }
}
.is-line-100{
  width: 100%;
  height: 44px;
}
.input-wrapper {
  width: 100%;
  .dp__main {
    width: 100%;
  }
}
.is-indent{
  margin-bottom: 24px;
  gap: 10px;

  &:last-child {
    margin-bottom: 0px;
  }
}
.select-date {
  margin-bottom: 54px;
  @media screen and (max-width: 1000px) {
    flex-direction: column;
    gap: 50px;
  }
}
.select-cap {
  @media screen and (max-width: 1000px) {
    flex-direction: column;
  }
}
.is-small-indent{
  margin-bottom: 15px;
}
.error {
  display: flex;
  justify-content: center;
  margin-bottom: 10px;
  color: red;
}
.is-minmax{
  border: 1px solid #289566;
  border-radius: 12px;
  padding: 12px 15px 12px 12px;
  display: flex;
  gap: 4px;

}
.is-text-inside{
  color: var(--color-50);
  font-size: 13px;
  padding-right: 2px;
}
.is-calendar{
  border: 1px solid var(--color-80);
  border-radius: 12px;
  padding: 13px 15px 13px;
}
.mr-2 {
  margin-right: 10px;
}
.is-advanced {
  cursor: pointer;
  display: flex;
  justify-content: center;
  span {
    font-size: 14px;
    text-decoration: underline;
  }
}
.is-types {
  display: grid;
  grid-template-columns: 50% 50%;
  grid-template-areas: "a b" "c d";
  gap: 0px 10px;

  .label-section {
    grid-area: a;
  }
  .label-asset-type {
    grid-area: b;
  }
  .input-section {
    grid-area: c;
  }
  .input-asset-type {
    grid-area: d;
  }
  .label-name {
    font-size: 14px;
    margin-bottom: 8px;
    color: var(--color-50);
  }
  .is-select {
    margin-right: 10px;
  }
}
</style>